/**
 * A number, or a string containing a number.
 * @typedef {latitude: Number, longitude: Number} AddressCoordinate
 *
 * @typedef {
 *   id: String,
 *   address: String,
 *   zipCode: Number,
 *   city: String,
 *   countryCode: String,
 *   coordinates: AddressCoordinate,
 *   fullAddress: String
 * } AddressResult
 *
 * @typedef {addressResults: AddressResult[], selectedAddress: AddressResult} State
 */

import '@public/css/deas-address-search.scss';
import { SearchForm } from '@public/js/deas-address-search/search-components.js';

/**
 * Reactive class to handle state changes and re-render the search form.
 * @param {HTMLElement} _rootElement
 * @param {Object} initialState
 * @constructor
 */
function Reactive( _rootElement, initialState ) {
	let state = initialState;
	let onRerender = [];
	let rootElement = _rootElement;
	let originalHtml = rootElement.innerHTML;

	const preserveFocus = () => {
		const { activeElementId, activeSelectionRange } = state;

		const activeElement = activeElementId
			? rootElement.querySelector( `#${ state.activeElementId }` )
			: null;

		if ( !activeElement ) {
			return;
		}

		activeElement.focus();

		if ( activeSelectionRange ) {
			activeElement.setSelectionRange( ...activeSelectionRange );
		}
	};

	const rerender = () => {
		onRerender = [];
		rootElement.innerHTML = SearchForm( this );
		preserveFocus();
		onRerender.forEach( callback => callback( this ) );
	};

	Object.defineProperty( this, 'state', {
		get: () => state,
		set: ( value ) => {
			state = {
				...value,
				activeElementId: document.activeElement?.id,
				activeSelectionRange: document.activeElement ? [
					document.activeElement.selectionStart,
					document.activeElement.selectionEnd,
				] : null,
			};

			rerender();
		}
	} );

	Object.defineProperty( this, 'computed', {
		get: () => ( {
			query: String( state.query ).charAt( 0 ).toUpperCase() + String( state.query ).slice( 1 ),
			paperformPrefill: {
				id: state.selectedAddress?.id || '',
				fullAddress: state.selectedAddress?.fullAddress || '',
				address: state.selectedAddress?.address || '',
				zip: state.selectedAddress?.zipCode || '',
				city: state.selectedAddress?.city || '',
			},
		} ),
		set: () => {
			throw new Error( 'Computed properties cannot be set directly.' );
		}
	} );

	Object.defineProperty( this, 'onRerender', {
		get: () => onRerender,
		set: ( value ) => {
			onRerender = [ ...onRerender, value ];
		}
	} );

	Object.defineProperty( this, 'originalHtml', {
		get: () => originalHtml,
		set: ( value ) => originalHtml = value
	} );

	Object.defineProperty( this, 'rootElement', {
		get: () => rootElement,
		set: ( value ) => {
			if ( !( value instanceof HTMLElement ) ) {
				throw new Error( 'Root element must be an instance of HTMLElement.' )
			}

			rootElement = value;
		}
	} );

	rerender();
}

Flatsome.behavior( 'deas-address-search', {
	attach: () => {
		const initialState = {
			addressResults: [],
			selectedAddress: null,
			query: '',
		};
		const rootElements = [ ...document.getElementsByClassName( 'deas-address-search' ) ];
		rootElements.forEach( rootElement => new Reactive( rootElement, initialState ) )
	}
} );
